// Meeting.jsx - Added minimize/maximize for video call modal
// + Added gated Family Planning entry card (search "FAMILY PLANNING" for the 3 additions)
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MeetingControls from "./components/MeetingControls";
import FiltersPanel from "./components/FiltersPanel";
import UserList from "./components/UserList";
import ProfileComparison from "./components/ProfileComparison";
import QuestionPopup from "./components/QuestionPopup";
import { fetchProfiles } from "./api/profiles";
import {
  startMeeting,
  endMeeting,
  getAllMeetingResultsByUsers,
  getDateQuestions,
  logMeetingQuestion,
  getMeetingTrajectory,
  getActiveMeeting,
  getRelationshipStatus,
  requestRelationshipStatusChange,
  respondRelationshipStatusChange,
  cancelRelationshipStatusChange,
  updateRelationshipStatus,
} from "./api/meetings";
import "./Meeting.css";
import { toast } from "../../../components/common/Toast";
import ToolTips from "./components/ToolTips";
import { convertSnakeCaseInText, toNormalText } from "./utils/helpers";
import VideoCall from "./components/VideoCall";
import MeetingHeader from "./components/MeetingHeader";
import CompatibilityResults from "./components/CompatibilityResults";
import config from "../../../config";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaWindowMaximize } from "react-icons/fa";
import { MdCallEnd } from "react-icons/md";
// // --- FAMILY PLANNING (addition 1 of 3): isolated component, only rendered when gated ---
// import FamilyPlanningEntryCard from "./FamilyPlanning/FamilyPlanningEntryCard";


const defaultCaptureModes = {
  wearables: true,
  voice: true,
  camera: false,
};

// --- FAMILY PLANNING (addition 2 of 3): the only relationship stages that unlock the module ---
// Kept separate from compatibility logic on purpose — this array is the single
// source of truth for the gate and should match the statuses used on the
// Family Planning backend's eligibility check.
const FAMILY_PLANNING_STATUSES = ["engaged", "married", "children"];

const getContextConfig = (mode) => {
  switch (mode) {
    case "video_call":
      return {
        meetingContext: "video_call",
        captureModes: { wearables: true, voice: true, camera: true },
      };
    case "audio_call":
      return {
        meetingContext: "audio_call",
        captureModes: { wearables: true, voice: true, camera: false },
      };
    case "verbal_audio":
      return {
        meetingContext: "verbal_audio",
        captureModes: { wearables: true, voice: true, camera: false },
      };
    case "in_person":
    default:
      return {
        meetingContext: "in_person",
        captureModes: { wearables: true, voice: true, camera: false },
      };
  }
};

const Meeting = () => {
  const user = useSelector((state) => state.user.userInfo);
  const token = useSelector((state) => state.user.token);
  const navigate = useNavigate();

  // Existing states
  const [selectedUser, setSelectedUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [allMeetingsData, setAllMeetingsData] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState("latest");
  const [trajectoryLoading, setTrajectoryLoading] = useState(false);
  const [relationshipTrajectory, setRelationshipTrajectory] = useState(null);
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showToolTipPopup, setShowToolTipPopup] = useState(false);
  const [stateDetails, setStateDetails] = useState([]);
  const [showStartMeetingPopup, setShowStartMeetingPopup] = useState(false);
  const [isVideoMeeting, setIsVideoMeeting] = useState(null);
  const [selectedMeetingMode, setSelectedMeetingMode] = useState("in_person");
  const [startingMeeting, setStartingMeeting] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { pending_status, requested_by, requested_at }
  const [showStatusApprovalPopup, setShowStatusApprovalPopup] = useState(false);
  const [relationshipStatus, setRelationshipStatus] = useState("matched");

  // New states for video call modal
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  const [isVideoCallMinimized, setIsVideoCallMinimized] = useState(false);

  // New state for question timer
  const [nextQuestionTimer, setNextQuestionTimer] = useState(0);
  const [isTimerVisible, setIsTimerVisible] = useState(false);
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);

  // Upgrade-plan prompt (shown when startMeeting is blocked by plan/quota)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  const videoCallRef = useRef(null);
  const firstTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const askedQuestionsRef = useRef([]);
  const mountedRef = useRef(true);
  const questionIntervalDuration = 60; // 60 seconds between questions
  const firstQuestionDelay = 10; // 10 seconds before first question

  // Helper functions (unchanged)
  const normalizeQuestion = (q) => {
    if (!q) return null;
    return {
      ...q,
      prompt: q.prompt || q.text || q.question || "",
      resolvedCategory: q.signal_type || q.category || q.question_category || "unknown",
    };
  };

  const trendMeaning = (v) => {
    if (!v) return "";
    if (v === "improving") return "Improving over time.";
    if (v === "stable") return "Stable / consistent.";
    if (v === "draining") return "Requires more effort / trending down.";
    if (v === "insufficient_data") return "Not enough data yet.";
    return String(v);
  };

  const getWindowLabel = (windowKey) => {
    if (windowKey === "7d") return "7 days";
    if (windowKey === "30d") return "30 days";
    if (windowKey === "90d") return "90 days";
    return "selected period";
  };

  const stateMap = useMemo(() => {
    return (stateDetails || []).reduce((acc, item) => {
      if (item?.state) acc[item.state] = item.meaning;
      return acc;
    }, {});
  }, [stateDetails]);

  const sharedStateMeaning = (state) => {
    if (!state) return "";
    return stateMap[state] || "";
  };

  // API calls (unchanged)
  const checkActiveMeeting = async () => {
    if (!user?.id) return;

    try {
      const data = await getActiveMeeting(user.id);
      if (!mountedRef.current) return;

      if (data?.success && data?.active_meeting) {
        const activeMeet = data.active_meeting;

        setActiveMeeting({
          meeting_id: activeMeet.meeting_id,
          user_1: activeMeet.user_1,
          user_2: activeMeet.user_2,
          start_time: activeMeet.start_time,
          meeting_type: activeMeet.meeting_type,
          meeting_context: activeMeet.meeting_context,
          capture_modes: activeMeet.capture_modes || defaultCaptureModes,
          zoom_meeting_link: activeMeet.zoom_meeting_link || null,
        });

        setMeetingStartTime(activeMeet.start_time);

        const currentUserId = String(user.id);
        const isUser1 = String(activeMeet.user_1) === currentUserId;
        const isUser2 = String(activeMeet.user_2) === currentUserId;

        let otherUser = null;

        if (isUser1 && activeMeet.user_2_details) {
          otherUser = {
            user_id: activeMeet.user_2,
            first_name: activeMeet.user_2_details.first_name,
            last_name: activeMeet.user_2_details.last_name,
            profile_image: activeMeet.user_2_details.profile_image,
            profession: activeMeet.user_2_details.profession,
          };
        } else if (isUser2 && activeMeet.user_1_details) {
          otherUser = {
            user_id: activeMeet.user_1,
            first_name: activeMeet.user_1_details.first_name,
            last_name: activeMeet.user_1_details.last_name,
            profile_image: activeMeet.user_1_details.profile_image,
            profession: activeMeet.user_1_details.profession,
          };
        } else if (activeMeet.user_2_details) {
          otherUser = {
            user_id: activeMeet.user_2,
            first_name: activeMeet.user_2_details.first_name,
            last_name: activeMeet.user_2_details.last_name,
            profile_image: activeMeet.user_2_details.profile_image,
            profession: activeMeet.user_2_details.profession,
          };
        }

        if (otherUser) {
          setSelectedUser(otherUser);
          setProfiles((prev) => {
            const exists = prev.some(
              (p) => String(p.user_id) === String(otherUser.user_id)
            );
            return exists ? prev : [...prev, otherUser];
          });
        }
      }
    } catch (err) {
      console.error("Error checking active meeting:", err);
    }
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await fetchProfiles(user.id);
      if (data?.success) {
        setProfiles(data.users || []);
      } else {
        toast.error("Failed to load profiles");
      }
    } catch (err) {
      toast.error("Error loading profiles. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      setIsLoadingQuestions(true);
      const data = await getDateQuestions({
        user1: user.id,
        user2: selectedUser?.user_id,
        relationship_status: relationshipStatus || "matched",
      });
      if (data?.success && Array.isArray(data?.questions)) {
        setAvailableQuestions(data.questions.map(normalizeQuestion).filter(Boolean));
      }
    } catch (err) {
      console.error("Error loading questions:", err);
      toast.error("Failed to load questions");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const fetchAllResults = async () => {
    const user1 = user?.id;
    const user2 = selectedUser?.user_id;
    if (!user1 || !user2) return;

    try {
      setResultsLoading(true);
      const data = await getAllMeetingResultsByUsers(user1, user2);
      if (!mountedRef.current) return;

      if (data?.success) {
        setAllMeetingsData(data);
        setStateDetails(Array.isArray(data?.state_detail) ? data.state_detail : []);
        const firstComputed = Array.isArray(data.meetings) ? data.meetings.find((m) => !!m.result) : null;
        if (firstComputed?.result) {
          setLiveResult({
            result: firstComputed.result,
            updatedAt: firstComputed.updatedAt || null,
            meeting_id: firstComputed.meeting_id,
            source: firstComputed.source || "unknown",
          });
        } else {
          setLiveResult(null);
        }
      } else {
        setAllMeetingsData(null);
        setLiveResult(null);
      }
    } catch (err) {
      console.error("Error fetching results by users:", err);
      if (!mountedRef.current) return;
      setAllMeetingsData(null);
      setLiveResult(null);
    } finally {
      if (mountedRef.current) setResultsLoading(false);
    }
  };

  const fetchRelationshipTrajectory = async (windowKey) => {
    const user1 = user?.id;
    const user2 = selectedUser?.user_id;
    if (!user1 || !user2) return;

    try {
      setTrajectoryLoading(true);
      const data = await getMeetingTrajectory(user1, user2, windowKey);
      if (!mountedRef.current) return;
      setRelationshipTrajectory(data?.success ? data : null);
      if (Array.isArray(data?.state_detail)) {
        setStateDetails(data.state_detail);
      }
    } catch (err) {
      console.error("Error fetching relationship trajectory:", err);
      if (!mountedRef.current) return;
      setRelationshipTrajectory(null);
      toast.error("Failed to load relationship trajectory.");
    } finally {
      if (mountedRef.current) setTrajectoryLoading(false);
    }
  };

  // Timer functions
  const startTimer = (seconds) => {
    setNextQuestionTimer(seconds);
    setIsTimerVisible(true);

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      setNextQuestionTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          setIsTimerVisible(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setIsTimerVisible(false);
    setNextQuestionTimer(0);
  };

  // Event handlers
  const handleUserSelect = (u) => {
    if (activeMeeting) {
      toast.info("Cannot change selected user while meeting is active");
      return;
    }
    setSelectedUser(u);
  };

  const handleDeselectUser = () => {
    setSelectedUser(null);
    setAllMeetingsData(null);
    setLiveResult(null);
    setRelationshipTrajectory(null);
    setSelectedWindow("latest");
  };

  const handleStartMeeting = () => {
    if (!selectedUser?.user_id) {
      toast.error("Please select a user to start a meeting");
      return;
    }
    if (activeMeeting) {
      toast.error("You already have an active meeting. Please end it first.");
      return;
    }
    setIsVideoMeeting(null);
    setSelectedMeetingMode("in_person");
    setShowStartMeetingPopup(true);
  };

  const closeStartMeetingPopup = () => {
    if (startingMeeting) return;
    setShowStartMeetingPopup(false);
    setIsVideoMeeting(null);
    setSelectedMeetingMode("in_person");
  };

  const UPGRADE_REASONS = ["SERVICE_NOT_INCLUDED", "LIMIT_REACHED", "NO_SUBSCRIPTION"];

  const handleConfirmStartMeeting = async () => {
    if (!selectedUser?.user_id) {
      toast.error("Please select a user to start a meeting");
      return;
    }

    const { meetingContext, captureModes } = getContextConfig(selectedMeetingMode);

    try {
      setStartingMeeting(true);
      setMeetingStartTime(new Date().toISOString());

      const data = await startMeeting(user.id, selectedUser.user_id, "marriage", meetingContext, captureModes);

      if (data?.success) {
        setActiveMeeting({
          ...data.meeting,
          capture_modes: data.meeting?.capture_modes || captureModes,
          meeting_context: data.meeting?.meeting_context || meetingContext,
        });
        toast.success("Meeting started");
        setShowStartMeetingPopup(false);
        setIsFirstQuestion(true);

        // Show video call modal if meeting context is video or audio
        if (meetingContext === "video_call" || meetingContext === "audio_call") {
          setShowVideoCallModal(true);
          setIsVideoCallMinimized(false);
        }

        // Start the timer for the first question
        startTimer(firstQuestionDelay);
      } else if (UPGRADE_REASONS.includes(data?.reason)) {
        // Blocked by plan/quota — show the upgrade prompt instead of a plain toast
        setMeetingStartTime(null);
        setShowStartMeetingPopup(false);
        setUpgradeMessage(data.message);
        setShowUpgradePrompt(true);
      } else {
        setMeetingStartTime(null);
        toast.error(data?.message || "Failed to start meeting");
      }
    } catch (err) {
      setMeetingStartTime(null);
      // axios throws on 4xx/5xx — the JSON body from the backend lives in err.response.data
      const data = err?.response?.data;
      if (UPGRADE_REASONS.includes(data?.reason)) {
        setShowStartMeetingPopup(false);
        setUpgradeMessage(data.message);
        setShowUpgradePrompt(true);
      } else {
        toast.error("Error starting meeting");
      }
      console.error("Error starting meeting:", err);
    } finally {
      setStartingMeeting(false);
    }
  };

  const sendFinalAnalyticsBeforeEnd = async (meetingId, u1, u2) => {
    if (
      !videoCallRef.current ||
      typeof videoCallRef.current.getAnalyticsData !== "function"
    ) {
      console.log("VideoCall ref not available");
      return;
    }

    const analyticsData = videoCallRef.current.getAnalyticsData() || {};
    const cameraMetrics = analyticsData.cameraMetrics || {};
    const voiceMetrics = analyticsData.voiceMetrics || {};

    const nowTs = Math.floor(Date.now() / 1000);

    const ensureMetric = (metrics, fallbackValue = 0.5) => {
      const out = {};

      Object.keys(metrics || {}).forEach((key) => {
        out[key] =
          Array.isArray(metrics[key]) && metrics[key].length > 0
            ? metrics[key]
            : [
              {
                ts: nowTs,
                value: key === "interrupt_patterns" ? 0 : fallbackValue,
              },
            ];
      });

      return out;
    };

    const basePayload = {
      user1: String(u1),
      user2: String(u2),
      senderUserId: String(user.id),
    };

    await fetch(`${config.baseURL}/api/meetings/${meetingId}/camera-live`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...basePayload,
        metrics: ensureMetric(cameraMetrics),
      }),
    });

    await fetch(`${config.baseURL}/api/meetings/${meetingId}/voice-live`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...basePayload,
        metrics: ensureMetric(voiceMetrics),
      }),
    });

    console.log("Final camera-live and voice-live sent before end meeting");
  };

  const handleEndMeeting = async () => {
    if (!activeMeeting?.meeting_id) return;

    try {
      const u1 = String(activeMeeting.user_1);
      const u2 = String(activeMeeting.user_2);
      const meetingId = activeMeeting.meeting_id;

      setResultsLoading(true);

      // First send camera + voice final data
      await sendFinalAnalyticsBeforeEnd(meetingId, u1, u2);

      // Then end meeting
      const data = await endMeeting(meetingId, u1, u2);

      if (data?.success) {
        toast.success("Meeting ended successfully");

        setShowVideoCallModal(false);
        setIsVideoCallMinimized(false);

        if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        stopTimer();

        setShowQuestionPopup(false);
        setCurrentQuestion(null);
        setActiveMeeting(null);
        setMeetingStartTime(null);
        setIsFirstQuestion(true);

        setTimeout(async () => {
          if (!mountedRef.current) return;

          await fetchAllResults();

          if (selectedWindow !== "latest") {
            await fetchRelationshipTrajectory(selectedWindow);
          }
        }, 3000);
      } else {
        toast.error(data?.message || "Failed to end meeting");
      }
    } catch (err) {
      toast.error("Error ending meeting");
      console.error("Error ending meeting:", err);
    } finally {
      if (mountedRef.current) setResultsLoading(false);
    }
  };

  const toggleVideoCallMinimize = () => {
    setIsVideoCallMinimized(prev => !prev);
  };

  useEffect(() => {
    const loadRelationshipStatus = async () => {
      if (!user?.id || !selectedUser?.user_id) return;

      try {
        const data = await getRelationshipStatus(user.id, selectedUser.user_id);

        if (data?.success) {
          setRelationshipStatus(data.relationship_status || "matched");
        }
      } catch (err) {
        console.error("Relationship status load error:", err);
      }
    };

    loadRelationshipStatus();
  }, [user?.id, selectedUser?.user_id]);

  // Effects
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { if (user?.id) { checkActiveMeeting(); loadProfiles(); } }, []);
  useEffect(() => { if (user?.id && selectedUser?.user_id) fetchAllResults(); }, [user?.id, selectedUser?.user_id]);
  useEffect(() => { if (selectedWindow !== "latest") fetchRelationshipTrajectory(selectedWindow); }, [selectedWindow]);
/// Poll for relationship status (and any pending request) while a user is selected.
// Runs continuously but at a low frequency so it stays cheap.
// Load questions ONLY when a person is selected (and reload if status changes)
useEffect(() => {
  if (!user?.id || !selectedUser?.user_id) {
    setAvailableQuestions([]);
    return;
  }
  loadQuestions();
}, [user?.id, selectedUser?.user_id, relationshipStatus]);
// Fetch status ONCE when a person is selected. No polling.
useEffect(() => {
  if (!user?.id || !selectedUser?.user_id) {
    setPendingStatusChange(null);
    return;
  }

  let cancelled = false;

  (async () => {
    try {
      const data = await getRelationshipStatus(user.id, selectedUser.user_id);
      if (cancelled || !data?.success) return;
      setRelationshipStatus(data.relationship_status || "matched");
      setPendingStatusChange(data.pending_change || null);
    } catch (err) {
      console.error("Relationship status load error:", err);
    }
  })();

  return () => { cancelled = true; };
}, [user?.id, selectedUser?.user_id]);




  // Show the approval popup only for the user who did NOT request the change
  useEffect(() => {
    if (pendingStatusChange && String(pendingStatusChange.requested_by) !== String(user?.id)) {
      setShowStatusApprovalPopup(true);
    } else {
      setShowStatusApprovalPopup(false);
    }
  }, [pendingStatusChange, user?.id]);

  useEffect(() => {
    if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowQuestionPopup(false);
    setCurrentQuestion(null);
    setAskedQuestionIds([]);
    askedQuestionsRef.current = [];
    stopTimer();

    const meetingId = activeMeeting?.meeting_id;
    const questionsReady = Array.isArray(availableQuestions) && availableQuestions.length > 0;

    if (!meetingId || !questionsReady) return;

    const askQuestion = () => {
      if (showQuestionPopup) return;
      if (!Array.isArray(availableQuestions) || availableQuestions.length === 0) return;

      const MAX_QUESTIONS = 10;
      const already = askedQuestionsRef.current || [];
      if (already.length >= MAX_QUESTIONS) return;

      const remaining = availableQuestions.filter((q) => !already.includes(q.id));
      if (!remaining.length) return;

      const random = remaining[Math.floor(Math.random() * remaining.length)];
      if (!random?.prompt) return;

      // Set the question - both users will see it
      setCurrentQuestion(random);
      setShowQuestionPopup(true);
      setAskedQuestionIds((prev) => {
        const next = [...prev, random.id];
        askedQuestionsRef.current = next;
        return next;
      });

      setIsFirstQuestion(false);

      // Log that both users should see this question
      console.log(`Question ${random.id} shown to both users`);
    };


    const scheduleNextQuestion = () => {
      askQuestion();
      // Start timer for next question after this one
      startTimer(questionIntervalDuration);
    };

    // Initial delay for first question
    firstTimeoutRef.current = setTimeout(() => {
      scheduleNextQuestion();
      // Set up interval for subsequent questions
      intervalRef.current = setInterval(scheduleNextQuestion, questionIntervalDuration * 1000);
    }, firstQuestionDelay * 1000);

    return () => {
      if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      stopTimer();
    };
  }, [activeMeeting?.meeting_id, availableQuestions, questionIntervalDuration, firstQuestionDelay]);

const handleRelationshipStatusChange = async (e) => {
  const newStatus = e.target.value;
  if (!user?.id || !selectedUser?.user_id || newStatus === relationshipStatus) return;

  try {
    const data = await requestRelationshipStatusChange({
      user1: user.id,
      user2: selectedUser.user_id,
      relationship_status: newStatus,
      requested_by: user.id,
    });

    if (data?.success) {
      setPendingStatusChange(data.pending_change || null);
      toast.success("Request sent, waiting for approval");
    } else {
      toast.error(data?.message || "Failed to request status change");
    }
  } catch (err) {
    console.error("Relationship status request error:", err);
    toast.error("Error requesting status change");
  }
};

const handleCancelStatusRequest = async () => {
  if (!user?.id || !selectedUser?.user_id) return;
  try {
    const data = await cancelRelationshipStatusChange({
      user1: user.id,
      user2: selectedUser.user_id,
      requested_by: user.id,
    });
    if (data?.success) {
      setPendingStatusChange(null);
      toast.info("Request cancelled");
    } else {
      toast.error(data?.message || "Failed to cancel request");
    }
  } catch (err) {
    console.error("Cancel status request error:", err);
    toast.error("Error cancelling request");
  }
};

const handleRespondStatusRequest = async (approve) => {
  if (!user?.id || !selectedUser?.user_id) return;
  try {
    const data = await respondRelationshipStatusChange({
      user1: user.id,
      user2: selectedUser.user_id,
      responder_id: user.id,
      approve,
    });
    if (data?.success) {
      setRelationshipStatus(data.relationship_status || relationshipStatus);
      setPendingStatusChange(null);
      setShowStatusApprovalPopup(false);
      toast.success(approve ? "Status updated" : "Status change declined");
      if (approve) await loadQuestions();
    } else {
      toast.error(data?.message || "Failed to respond to request");
    }
  } catch (err) {
    console.error("Respond status request error:", err);
    toast.error("Error responding to request");
  }
};
  const handleQuestionSubmit = async () => {
    if (!activeMeeting?.meeting_id || !currentQuestion || !user?.id) return;

    try {
      const payload = {
        asked_by_user_id: user.id,
        question_id: currentQuestion.id,
        question_category: currentQuestion.resolvedCategory || "unknown",
        asked_at: new Date().toISOString(),
      };
      await logMeetingQuestion(activeMeeting.meeting_id, payload);
      toast.success("Question logged successfully");
    } catch (err) {
      console.error("Error saving question:", err);
      toast.error("Failed to save question");
    } finally {
      setShowQuestionPopup(false);
    }
  };

  const filteredProfiles = profiles.filter((profile) => {
    if (genderFilter !== "all" && profile.gender !== genderFilter) return false;
    if (nameFilter.trim() !== "") {
      const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
      const searchTerm = nameFilter.toLowerCase();
      return fullName.includes(searchTerm) || profile.first_name.toLowerCase().includes(searchTerm) || profile.last_name.toLowerCase().includes(searchTerm);
    }
    return true;
  });

  const shouldShowVideoCall = activeMeeting && (activeMeeting.meeting_context === "video_call" || activeMeeting.meeting_context === "audio_call");
  const isLoading = selectedWindow === "latest" ? resultsLoading : trajectoryLoading;

  if (!user) return <div className="meeting-container">Loading user data...</div>;

  return (
    <div className="meeting-container">
      <div className="meeting-layout-redesign">
        {/* Left Panel */}
        <section className="left-panel-redesign">
          <div className="user-list-section">
            <div className="section-header">
              <h2>Friends List</h2>
              <button onClick={loadProfiles} className="refresh-btn" disabled={loading}>
                {loading ? "Loading..." : "↻ Refresh"}
              </button>
            </div>

            <FiltersPanel
              genderFilter={genderFilter}
              onGenderFilter={setGenderFilter}
              nameFilter={nameFilter}
              onNameFilter={setNameFilter}
            />

            {activeMeeting && (
              <div className="meeting-active-warning">
                <span className="warning-icon">⚠️</span>
                <span>
                  Meeting in progress with {selectedUser?.first_name} {selectedUser?.last_name}
                  {activeMeeting?.meeting_context ? ` (${toNormalText(activeMeeting.meeting_context)})` : ""}
                </span>
              </div>
            )}

            {loading ? (
              <div className="loading">Loading users...</div>
            ) : filteredProfiles.length > 0 ? (
              <UserList
                profiles={filteredProfiles}
                selectedUserId={selectedUser?.user_id}
                onUserSelect={handleUserSelect}
                activeMeeting={activeMeeting}
                isSelectionDisabled={!!activeMeeting}
              />
            ) : (
              <div className="no-users">No users found. Try changing filters.</div>
            )}
          </div>

        </section>

        {/* Main Content */}
        <section className="main-content-redesign">
          <div className="profile-comparison-container">
            <ProfileComparison
              currentUser={user}
              selectedUser={selectedUser}
              activeMeeting={activeMeeting}
              meetingStartTime={meetingStartTime}
              profiles={filteredProfiles}
              selectedUserId={selectedUser?.user_id}
              onUserSelect={handleUserSelect}
              onDeselectUser={handleDeselectUser}
              isSelectionDisabled={!!activeMeeting}
            />
            {selectedUser && (
              <div className="relationship-status-box">
                <label>Relationship Status</label>

                {pendingStatusChange && String(pendingStatusChange.requested_by) === String(user.id) ? (
                  <div className="relationship-status-pending">
                    <span>
                      Waiting for approval: {toNormalText(relationshipStatus)} → {toNormalText(pendingStatusChange.pending_status)}
                    </span>
                    <button className="cancel-status-btn" onClick={handleCancelStatusRequest}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <select
                    value={relationshipStatus}
                    onChange={handleRelationshipStatusChange}
                    className="relationship-status-select"
                    disabled={!!pendingStatusChange}
                  >
                    <option value="matched">Matched</option>
                    <option value="dating">Dating</option>
                    <option value="engaged">Engaged</option>
                    <option value="married">Married</option>
                    <option value="children">Children</option>
                    <option value="long_term">Long-term Tracking</option>
                  </select>
                )}
              </div>
            )}

            {/* --- FAMILY PLANNING (addition 3 of 3): gated entry card ---
                Renders only for this dyad, only once relationshipStatus qualifies,
                and only as a link out to the separate /family-planning route.
                It never reads or writes anything from liveResult, allMeetingsData,
                or relationshipTrajectory above. */}
            {/* {selectedUser && FAMILY_PLANNING_STATUSES.includes(relationshipStatus) && (
              <FamilyPlanningEntryCard
                user1Id={user.id}
                user2Id={selectedUser.user_id}
                partnerName={`${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim()}
              />
            )} */}
          </div>

          <div className="meeting-controls-container">
            <MeetingControls
              selectedUser={selectedUser}
              currentUser={user}
              activeMeeting={activeMeeting}
              onStartMeeting={handleStartMeeting}
              onEndMeeting={handleEndMeeting}
              meetingStartTime={meetingStartTime}
              onDeselectUser={handleDeselectUser}
            />
          </div>

          {/* Inline video call removed – now handled by modal */}

          <CompatibilityResults
            selectedUser={selectedUser}
            liveResult={liveResult}
            allMeetingsData={allMeetingsData}
            relationshipTrajectory={relationshipTrajectory}
            selectedWindow={selectedWindow}
            setSelectedWindow={setSelectedWindow}
            isLoading={isLoading}
            user={user}
            getWindowLabel={getWindowLabel}
            trendMeaning={trendMeaning}
            sharedStateMeaning={sharedStateMeaning}
            convertSnakeCaseInText={convertSnakeCaseInText}
            toNormalText={toNormalText}
            setShowToolTipPopup={setShowToolTipPopup}
            trajectoryLoading={trajectoryLoading}
          />
        </section>
      </div>

      {showStartMeetingPopup && (
        <MeetingHeader
          selectedUser={selectedUser}
          isVideoMeeting={isVideoMeeting}
          setIsVideoMeeting={setIsVideoMeeting}
          selectedMeetingMode={selectedMeetingMode}
          setSelectedMeetingMode={setSelectedMeetingMode}
          startingMeeting={startingMeeting}
          handleConfirmStartMeeting={handleConfirmStartMeeting}
          closeStartMeetingPopup={closeStartMeetingPopup}
        />
      )}

      {showQuestionPopup && currentQuestion && (
        <QuestionPopup
          question={currentQuestion}
          onClose={() => setShowQuestionPopup(false)}
          onSubmit={handleQuestionSubmit}
        />
      )}
      {showStatusApprovalPopup && pendingStatusChange && (
  <div className="status-approval-popup-overlay">
    <div className="status-approval-popup">
      <h3>Relationship Status Change</h3>
      <p>
        {selectedUser?.first_name} wants to change your relationship status from{" "}
        <strong>{toNormalText(relationshipStatus)}</strong> to{" "}
        <strong>{toNormalText(pendingStatusChange.pending_status)}</strong>.
      </p>
      <div className="status-approval-actions">
        <button className="approve-btn" onClick={() => handleRespondStatusRequest(true)}>
          Yes
        </button>
        <button className="reject-btn" onClick={() => handleRespondStatusRequest(false)}>
          No
        </button>
      </div>
    </div>
  </div>
)}

      {showUpgradePrompt && (
        <div className="status-approval-popup-overlay">
          <div className="status-approval-popup">
            <h3>Upgrade required</h3>
            <p>{upgradeMessage}</p>
            <div className="status-approval-actions">
              <button
                className="approve-btn"
                onClick={() => {
                  setShowUpgradePrompt(false);
                  navigate("/upgrade-profile");
                }}
              >
                Upgrade plan
              </button>
              <button className="reject-btn" onClick={() => setShowUpgradePrompt(false)}>
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {showToolTipPopup && (
        <ToolTips stateDetails={stateDetails} onClose={() => setShowToolTipPopup(false)} />
      )}

      {/* Question Timer - Bottom Left Corner */}
      {isTimerVisible && activeMeeting && (
        <div className="question-timer-container">
          <div className="question-timer">
            <div className="timer-icon">⏳</div>
            <div className="timer-content">
              <div className="timer-label">
                {isFirstQuestion ? 'First question in' : 'Next question in'}
              </div>
              <div className="timer-countdown">{nextQuestionTimer}s</div>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Modal with Minimize/Maximize */}
      {showVideoCallModal && activeMeeting && shouldShowVideoCall && (
        <div className={`video-call-modal-overlay ${isVideoCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className={`video-call-modal-container ${isVideoCallMinimized ? 'minimized-container' : ''}`}>
            <VideoCall
              ref={videoCallRef}
              roomId={String(activeMeeting.meeting_id)}
              userId={user?.id}
              token={token}
              meetingId={activeMeeting.meeting_id}
              user={user}
              selectedUser={selectedUser}
              user1Id={activeMeeting.user_1}
              user2Id={activeMeeting.user_2}
              onMeetingEnd={() => {
                handleEndMeeting()
                setTimeout(() => {
                  fetchAllResults();
                  if (selectedWindow !== "latest") fetchRelationshipTrajectory(selectedWindow);
                }, 1000);
              }}
              isMinimized={isVideoCallMinimized}
              onToggleMinimize={toggleVideoCallMinimize}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Meeting;